import { useState, useEffect, useRef } from 'react';
import { isNativeApp } from '../../utils/platform';
import {
  analyzeListingURL,
  analyzePhotos,
  analyzeGeneralVehicle,
  parseAnalysisResponse,
} from '../../services/autoAdvisorAI';
import { getChecklistState, toggleChecklistItem } from '../../utils/storage';
import Chat from '../chat/Chat';

const STAGES = [
  'Identifying vehicle...',
  'Researching market values...',
  'Analyzing comparable sales...',
  'Checking common issues...',
  'Building cost projections...',
  'Preparing your report...',
];

/* ───────────────────── Mock data generator ───────────────────── */

function generateMockResults(data) {
  const vehicleLabel = data.hasSpecificListing
    ? 'Vehicle from listing'
    : `${data.year} ${data.make} ${data.model}${data.trim ? ` ${data.trim}` : ''}`;

  return {
    vehicleLabel,
    sections: [
      {
        id: 'valuation',
        title: 'Market Valuation',
        emoji: '\uD83D\uDCB0',
        content: {
          range: '$28,000 \u2013 $35,000',
          trend: 'stable',
          trendNote: 'Prices have held steady over the last 6 months.',
          comparables: [
            'BaT: $31,500 \u2013 2022, similar spec, 18k miles (Jan 2026)',
            'Cars & Bids: $29,200 \u2013 2021, higher mileage (Dec 2025)',
            'eBay Motors: $33,800 \u2013 2022, low miles, excellent condition (Feb 2026)',
          ],
          verdict: data.askingPrice
            ? `At an asking price of ${data.askingPrice}, this appears to be within the typical market range.`
            : "Request the seller's asking price to compare against market data.",
        },
      },
      {
        id: 'perspectives',
        title: 'Vehicle Perspectives',
        emoji: '\uD83E\uDDE0',
        content: {
          perspectives: [
            { key: 'consumer', emoji: '\uD83D\uDC64', label: 'Consumer Take', text: 'A solid choice for someone wanting reliability with a touch of excitement. Resale value is strong, so your money is well-protected. Insurance is slightly higher than mainstream brands but parts availability is good.' },
            { key: 'enthusiast', emoji: '\uD83C\uDFCE\uFE0F', label: 'Enthusiast Take', text: 'The driving dynamics are a highlight — sharp steering, willing engine, and a chassis that rewards spirited driving. The aftermarket community is active with plenty of bolt-on options. This is a car you look forward to driving.' },
            { key: 'collector', emoji: '\uD83C\uDFC6', label: 'Collector Take', text: 'Not yet a blue-chip collectible, but certain specs and colors are starting to command premiums. Low-mile, well-documented examples will likely appreciate. The enthusiast following ensures sustained demand.' },
            { key: 'mechanic', emoji: '\uD83D\uDD27', label: 'Mechanic Take', text: 'Generally reliable with proper maintenance. Watch for turbo-related issues on certain model years. Oil changes on shorter intervals recommended. Common wear items are reasonably priced but labor can be higher at specialists.' },
          ],
        },
      },
      {
        id: 'usability',
        title: 'Daily vs Weekend Use',
        emoji: '\uD83D\uDE97',
        content: {
          dailyScore: 6,
          dailyNote: 'Comfortable enough for commuting but premium fuel and firm ride may feel like compromises for daily use.',
          weekendScore: 9,
          weekendNote: 'This is where the car truly shines — engaging dynamics, head-turning presence, and a rewarding driving experience on twisty roads.',
        },
      },
      ...(data.listingPhotos.length > 0
        ? [
            {
              id: 'photos',
              title: 'Photo Analysis',
              emoji: '\uD83D\uDCF8',
              content: {
                summary: `${data.listingPhotos.length} photo(s) uploaded for review.`,
                observations: [
                  'Exterior appears to be in good condition based on provided images',
                  'Recommend requesting additional photos of: undercarriage, engine bay, tire tread depth',
                  'Check for paint correction or respray evidence in person',
                ],
              },
            },
          ]
        : []),
      {
        id: 'checklist',
        title: 'Pre-Purchase Checklist',
        emoji: '\u2705',
        content: {
          items: [
            {
              text: 'Request a pre-purchase inspection (PPI) from an independent specialist',
              priority: 'high',
            },
            {
              text: 'Verify service history with authorized dealer records',
              priority: 'high',
            },
            { text: 'Check for open recalls via NHTSA', priority: 'high' },
            {
              text: 'Run a vehicle history report (Carfax / AutoCheck)',
              priority: 'high',
            },
            {
              text: 'Inspect for signs of accident repair or panel gaps',
              priority: 'medium',
            },
            {
              text: 'Test all electronics, infotainment, and driver assists',
              priority: 'medium',
            },
            { text: 'Check tire date codes and tread depth', priority: 'low' },
            { text: 'Verify spare key availability', priority: 'low' },
          ],
        },
      },
      {
        id: 'cost',
        title: 'Cost-to-Own (First Year)',
        emoji: '\uD83D\uDCCA',
        content: {
          items: [
            { label: 'Insurance', rating: 'Above Average', note: 'Sports car classification increases premiums' },
            { label: 'Scheduled Maintenance', rating: 'Above Average', note: 'Premium brand service intervals and parts costs' },
            { label: 'Fuel', rating: 'Average', note: 'Premium fuel required, moderate consumption' },
            { label: 'Registration & Taxes', rating: 'Average', note: 'Standard rates for this vehicle class' },
            { label: 'Depreciation', rating: 'Below Average', note: 'Strong resale value holds well in this segment' },
          ],
          summary: 'Overall ownership costs are slightly above average for the segment, offset by strong resale value.',
        },
      },
      {
        id: 'insights',
        title: 'Expert Insights',
        emoji: '\uD83D\uDD27',
        content: {
          tips: [
            'This model responds well to regular fluid changes on shorter-than-factory intervals.',
            'Look for examples with documented dealer service history over independent shop records.',
            'Low-mile examples that were garage-kept command a significant premium.',
            'The enthusiast community for this model is active and knowledgeable \u2014 join forums before buying.',
          ],
        },
      },
      {
        id: 'negotiation',
        title: 'Negotiation Tips',
        emoji: '\uD83E\uDD1D',
        content: {
          tips: [
            'Use comparable recent sales data to justify your offer price.',
            'Factor in any upcoming maintenance items (tires, brakes, fluids) as negotiation points.',
            'Ask for maintenance records \u2014 incomplete records justify a lower offer.',
            'If buying from a dealer, negotiate the out-the-door price, not just the sticker.',
            'Private party sales often have room for 5\u201310% below asking.',
          ],
        },
      },
    ],
  };
}

/* ───────────────────── Section sub-head style ───────────────────── */

const sectionSubheadStyle = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 8,
};

/* ───────────────────── Section content renderer ───────────────────── */

/* ───────────────────── Interactive checklist ───────────────────── */

function ChecklistContent({ items, reportId }) {
  const [checked, setChecked] = useState(() => getChecklistState(reportId));

  function toggle(index) {
    const updated = toggleChecklistItem(reportId, index);
    setChecked({ ...updated });
  }

  const completedCount = items.filter((_, i) => checked[i]).length;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ flex: 1, height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
          <div
            style={{
              width: items.length > 0 ? `${(completedCount / items.length) * 100}%` : '0%',
              height: '100%',
              background: 'var(--accent)',
              borderRadius: 2,
              transition: 'width 0.3s ease',
            }}
          />
        </div>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {completedCount}/{items.length}
        </span>
      </div>
      {items.map((item, i) => {
        const isChecked = !!checked[i];
        return (
          <button
            key={i}
            onClick={() => toggle(i)}
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              marginBottom: 10,
              width: '100%',
              textAlign: 'left',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
            }}
          >
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: 4,
                border: isChecked ? 'none' : '1.5px solid var(--border)',
                background: isChecked ? 'var(--accent)' : 'transparent',
                flexShrink: 0,
                marginTop: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
              }}
            >
              {isChecked && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <span
                style={{
                  fontSize: '0.8125rem',
                  color: isChecked ? 'var(--text-muted)' : 'var(--text-primary)',
                  lineHeight: 1.4,
                  textDecoration: isChecked ? 'line-through' : 'none',
                  transition: 'all 0.15s ease',
                }}
              >
                {item.text}
              </span>
              <span
                className={`badge badge-${item.priority}`}
                style={{ marginLeft: 8, fontSize: '0.625rem', opacity: isChecked ? 0.5 : 1 }}
              >
                {item.priority}
              </span>
            </div>
          </button>
        );
      })}
    </>
  );
}

/* ───────────────────── Section content renderer ───────────────────── */

function SectionContent({ section, reportId }) {
  const { id, content } = section;

  if (id === 'valuation') {
    return (
      <>
        <div style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 8 }}>
          {content.range}
        </div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: content.trend === 'declining' ? 'var(--warning)' : 'var(--success)',
            fontWeight: 600,
            fontSize: '0.875rem',
            marginBottom: 12,
          }}
        >
          <span>{content.trend === 'appreciating' ? '↑' : content.trend === 'declining' ? '↓' : '→'}</span>
          <span style={{ textTransform: 'capitalize' }}>{content.trend}</span>
        </div>
        <p
          style={{
            fontSize: '0.8125rem',
            color: 'var(--text-secondary)',
            marginBottom: 12,
          }}
        >
          {content.trendNote}
        </p>
        {content.comparables.length > 0 && (
          <>
            <h4 style={sectionSubheadStyle}>Recent Comparables</h4>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {content.comparables.map((c, i) => (
                <li
                  key={i}
                  style={{
                    fontSize: '0.8125rem',
                    color: 'var(--text-primary)',
                    paddingLeft: 12,
                    borderLeft: '2px solid var(--accent)',
                    lineHeight: 1.4,
                  }}
                >
                  {c}
                </li>
              ))}
            </ul>
          </>
        )}
        {content.verdict && (
          <p
            style={{
              fontSize: '0.875rem',
              color: 'var(--text-primary)',
              fontWeight: 500,
              marginTop: 12,
              padding: 12,
              background: 'var(--accent-subtle)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            {content.verdict}
          </p>
        )}
      </>
    );
  }

  if (id === 'perspectives') {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 10,
        }}
      >
        {content.perspectives.map((p) => (
          <div
            key={p.key}
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: 12,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 8,
              }}
            >
              <span style={{ fontSize: '1rem' }}>{p.emoji}</span>
              <span
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                }}
              >
                {p.label}
              </span>
            </div>
            <p
              style={{
                fontSize: '0.75rem',
                color: 'var(--text-secondary)',
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              {p.text}
            </p>
          </div>
        ))}
      </div>
    );
  }

  if (id === 'usability') {
    const scoreColor = (score) =>
      score >= 7 ? 'var(--success)' : score <= 3 ? 'var(--text-muted)' : 'var(--accent)';
    return (
      <div style={{ display: 'flex', gap: 12 }}>
        {[
          { label: 'Daily Driver', score: content.dailyScore, note: content.dailyNote },
          { label: 'Weekend Toy', score: content.weekendScore, note: content.weekendNote },
        ].map((item) => (
          <div
            key={item.label}
            style={{
              flex: 1,
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: 14,
            }}
          >
            <div
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                marginBottom: 6,
              }}
            >
              {item.label}
            </div>
            <div
              style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: scoreColor(item.score),
                marginBottom: 8,
              }}
            >
              {item.score}<span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-muted)' }}>/10</span>
            </div>
            <div
              style={{
                height: 6,
                borderRadius: 3,
                background: 'var(--border)',
                marginBottom: 10,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${(item.score / 10) * 100}%`,
                  height: '100%',
                  borderRadius: 3,
                  background: scoreColor(item.score),
                  transition: 'width 0.5s ease',
                }}
              />
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.4, margin: 0 }}>
              {item.note}
            </p>
          </div>
        ))}
      </div>
    );
  }

  if (id === 'photos') {
    return (
      <>
        <p
          style={{
            fontSize: '0.8125rem',
            color: 'var(--text-secondary)',
            marginBottom: 12,
          }}
        >
          {content.summary}
        </p>
        {content.observations.map((obs, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 8,
              marginBottom: 8,
              fontSize: '0.8125rem',
            }}
          >
            <span style={{ color: 'var(--accent)', flexShrink: 0 }}>
              &bull;
            </span>
            <span style={{ color: 'var(--text-primary)', lineHeight: 1.4 }}>
              {obs}
            </span>
          </div>
        ))}
      </>
    );
  }

  if (id === 'checklist') {
    return <ChecklistContent items={content.items} reportId={reportId} />;
  }

  if (id === 'cost') {
    const ratingColor = (rating) => {
      if (rating === 'Below Average') return 'var(--success)';
      if (rating === 'Above Average') return 'var(--warning)';
      return 'var(--text-muted)';
    };
    const ratingBg = (rating) => {
      if (rating === 'Below Average') return 'rgba(34, 197, 94, 0.12)';
      if (rating === 'Above Average') return 'rgba(245, 158, 11, 0.12)';
      return 'rgba(255, 255, 255, 0.06)';
    };
    return (
      <>
        {content.items.map((item, i) => (
          <div
            key={i}
            style={{
              padding: '10px 0',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                {item.label}
              </span>
              <span
                style={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: ratingColor(item.rating),
                  background: ratingBg(item.rating),
                  padding: '3px 10px',
                  borderRadius: 100,
                  whiteSpace: 'nowrap',
                }}
              >
                {item.rating}
              </span>
            </div>
            {item.note && (
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 }}>
                {item.note}
              </p>
            )}
          </div>
        ))}
        {content.summary && (
          <p
            style={{
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: 'var(--text-secondary)',
              marginTop: 12,
              lineHeight: 1.5,
            }}
          >
            {content.summary}
          </p>
        )}
      </>
    );
  }

  if (id === 'insights') {
    return (
      <>
        {content.tips.map((tip, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 8,
              marginBottom: 10,
              fontSize: '0.8125rem',
            }}
          >
            <span
              style={{
                color: 'var(--accent)',
                flexShrink: 0,
                fontWeight: 700,
              }}
            >
              {i + 1}.
            </span>
            <span style={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>
              {tip}
            </span>
          </div>
        ))}
      </>
    );
  }

  if (id === 'negotiation') {
    if (content.isDealerListing === false) {
      return (
        <div
          style={{
            padding: '14px 16px',
            background: 'var(--bg-elevated)',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
          }}
        >
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
            Negotiation tips are most relevant for dealership listings. For private party sales,
            focus on comparable sales data in the Market Valuation section above.
          </p>
        </div>
      );
    }
    return (
      <>
        {content.tips.map((tip, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 8,
              marginBottom: 10,
              fontSize: '0.8125rem',
            }}
          >
            <span
              style={{
                color: 'var(--accent)',
                flexShrink: 0,
                fontWeight: 700,
              }}
            >
              {i + 1}.
            </span>
            <span style={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>
              {tip}
            </span>
          </div>
        ))}
      </>
    );
  }

  return null;
}

/* ───────────────────── Expandable result section ───────────────────── */

function ResultSection({ section, reportId }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '1.25rem' }}>{section.emoji}</span>
          <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>
            {section.title}
          </span>
        </div>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--text-muted)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transition: 'transform 0.2s',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
            flexShrink: 0,
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {expanded && (
        <div
          style={{
            padding: '0 20px 16px',
            borderTop: '1px solid var(--border)',
            paddingTop: 16,
            animation: 'fadeIn 0.2s ease',
          }}
        >
          <SectionContent section={section} reportId={reportId} />
        </div>
      )}
    </div>
  );
}

/* ───────────────────── Spinner styles ───────────────────── */

const spinnerContainerStyle = {
  width: 72,
  height: 72,
  margin: '0 auto 24px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const spinnerStyle = {
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  border: '2.5px solid var(--border)',
  borderTopColor: 'var(--accent)',
  animation: 'spin 1s linear infinite',
};

/* ───────────────────── Main component ───────────────────── */

export default function AnalysisResults({ data, onSave, onReset, onExit, onRefresh, onDelete }) {
  const [loading, setLoading] = useState(true);
  const [stageIndex, setStageIndex] = useState(0);
  const [results, setResults] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const abortRef = useRef(null);
  // Stable report ID for checklist persistence (use savedAt if viewing saved, or generate once)
  const reportIdRef = useRef(data.savedAt || `report_${Date.now()}`);
  const reportId = reportIdRef.current;

  useEffect(() => {
    // If viewing a saved report with pre-loaded results, skip the API call
    if (data.results) {
      setResults(data.results);
      setStageIndex(STAGES.length - 1);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    // Advance stages on a timer as a baseline animation
    const interval = setInterval(() => {
      setStageIndex((prev) => (prev < STAGES.length - 1 ? prev + 1 : prev));
    }, 3000);

    async function run() {
      try {
        const onStage = (stage) => {
          console.log('[AutoAdvisor] Stage:', stage);
        };

        let rawJSON;

        if (data.hasSpecificListing) {
          if (data.listingPhotos && data.listingPhotos.length > 0) {
            rawJSON = await analyzePhotos(data, controller.signal, onStage);
          } else {
            rawJSON = await analyzeListingURL(data, controller.signal, onStage);
          }
        } else {
          rawJSON = await analyzeGeneralVehicle(data, controller.signal, onStage);
        }

        if (controller.signal.aborted) return;

        const parsed = parseAnalysisResponse(rawJSON, data);
        setResults(parsed);
        setStageIndex(STAGES.length - 1);
        setLoading(false);
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'APIUserAbortError') return;
        console.error('[AutoAdvisor] Analysis failed:', err);

        // Show clear error messages for credit/rate limit issues
        if (err.message === 'NO_CREDITS') {
          setError('No credits remaining. Upgrade your plan for more.');
          setResults(generateMockResults(data));
          setStageIndex(STAGES.length - 1);
          setLoading(false);
          return;
        }
        if (err.message === 'RATE_LIMITED') {
          setError('Too many requests. Please wait a minute before trying again.');
          setResults(generateMockResults(data));
          setStageIndex(STAGES.length - 1);
          setLoading(false);
          return;
        }

        // Fall back to mock data on other errors
        const mockResults = generateMockResults(data);
        setResults(mockResults);
        setError(err.message || 'Analysis failed. Showing sample data.');
        setStageIndex(STAGES.length - 1);
        setLoading(false);
      }
    }

    run();

    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [data]);

  function handleSave() {
    onSave(results);
    setSaved(true);
  }

  /* ─── Loading phase ─── */
  if (loading) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={spinnerContainerStyle}>
            <div style={spinnerStyle} />
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--accent)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ position: 'absolute' }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>

          <h2
            style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 8 }}
          >
            Analyzing Vehicle
          </h2>
          <p
            style={{
              color: 'var(--text-secondary)',
              fontSize: '0.9375rem',
              marginBottom: 32,
            }}
          >
            Building your buyer&apos;s report
          </p>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              textAlign: 'left',
              maxWidth: 280,
            }}
          >
            {STAGES.map((label, i) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  opacity: i <= stageIndex ? 1 : 0.3,
                  transition: 'opacity 0.4s',
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    background:
                      i < stageIndex
                        ? 'var(--success)'
                        : i === stageIndex
                          ? 'var(--accent)'
                          : 'var(--bg-elevated)',
                    transition: 'background 0.3s',
                  }}
                >
                  {i < stageIndex ? (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : i === stageIndex ? (
                    <div
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: 'white',
                        animation: 'pulse 1s infinite',
                      }}
                    />
                  ) : null}
                </div>
                <span
                  style={{
                    fontSize: '0.875rem',
                    color:
                      i <= stageIndex
                        ? 'var(--text-primary)'
                        : 'var(--text-muted)',
                  }}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        `}</style>
      </div>
    );
  }

  /* ─── Results phase ─── */
  return (
    <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 200 }}>
      {/* Error banner */}
      {error && (
        <div
          style={{
            background: 'var(--bg-elevated)',
            border: `1px solid ${error.includes('credits') || error.includes('requests') ? 'var(--danger)' : 'var(--warning)'}`,
            borderRadius: 'var(--radius-sm)',
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: '0.8125rem',
            color: error.includes('credits') || error.includes('requests') ? 'var(--danger)' : 'var(--warning)',
          }}
        >
          {error.includes('credits') || error.includes('requests')
            ? error
            : `AI analysis unavailable — showing sample data. (${error})`}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 className="screen-title">{results.vehicleLabel}</h1>
        <p className="screen-subtitle" style={{ marginTop: 4 }}>
          Your Auto Advisor report is ready.
        </p>
      </div>

      {/* Result sections */}
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        {results.sections.map((section) => (
          <ResultSection key={section.id} section={section} reportId={reportId} />
        ))}
      </div>

      {/* Bottom CTAs — positioned above the bottom nav with safe area inset */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: 480,
          padding: '16px 20px',
          paddingBottom: 'calc(70px + env(safe-area-inset-bottom, 0px))',
          background: 'linear-gradient(transparent, var(--bg-primary) 25%)',
        }}
      >
        {/* "Ask a question" — primary follow-up CTA above the existing
            Save/Refresh/Delete actions. Opens the advisor chat overlay
            pre-loaded with the full report context (so the assistant
            can reference specific sections by name). */}
        <button
          className="btn btn-primary"
          onClick={() => setShowChat(true)}
          style={{
            width: '100%',
            marginBottom: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Ask a question
        </button>

        {onRefresh ? (
          /* Saved report mode — Refresh + Delete (demoted to secondary
             now that "Ask a question" is the featured action). */
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className="btn btn-secondary"
              onClick={onRefresh}
              style={{ flex: 1 }}
            >
              Refresh Report
            </button>
            <button
              className="btn btn-secondary"
              onClick={onDelete}
              style={{
                flex: 1,
                color: 'var(--danger, #ef4444)',
                borderColor: 'var(--danger, #ef4444)',
              }}
            >
              Delete Report
            </button>
          </div>
        ) : (
          /* Fresh analysis mode — Save + Analyze Another */
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className="btn btn-secondary"
              onClick={handleSave}
              disabled={saved}
              style={{ flex: 1 }}
            >
              {saved ? 'Saved \u2713' : 'Save Report'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={onReset}
              style={{ flex: 1 }}
            >
              Analyze Another
            </button>
          </div>
        )}
        <button
          className="btn btn-ghost"
          onClick={onExit}
          style={{ width: '100%', marginTop: 8 }}
        >
          Back to Main
        </button>
      </div>

      {/* Advisor follow-up chat overlay. Mounted only while open, so each
          tap of "Ask a question" reopens with the latest report data.
          The Chat component is position:fixed/z-50, so it overlays the
          full report screen without affecting layout. */}
      {showChat && (
        <Chat
          mode="advisor-purchase"
          advisorData={{ ...data, results }}
          onBack={() => setShowChat(false)}
          onToast={() => { /* no-op */ }}
        />
      )}
    </div>
  );
}
